import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Check, ChevronsUpDown, Search, X, Plus } from 'lucide-react';
import { cn } from '@/lib/utils';

// Options may be strings or { value, label, sub } objects. Normalized here.
function normalize(options) {
  return (options || []).map(o => (typeof o === 'string' ? { value: o, label: o } : o));
}
function byLabel(a, b) {
  return String(a.label).toLowerCase().localeCompare(String(b.label).toLowerCase());
}

// Single-value searchable select. Dropdown is rendered INLINE (not portalled)
// so it works correctly inside Radix Sheet/Dialog without closing the parent.
export function SearchableSelect({
  value, onChange, options, placeholder = 'Select…', searchPlaceholder = 'Search…',
  allowCustom = false, disabled = false, className, triggerClassName, testId, align = 'left',
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const opts = useMemo(() => normalize(options).slice().sort(byLabel), [options]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return s ? opts.filter(o => String(o.label).toLowerCase().includes(s) || String(o.value).toLowerCase().includes(s)) : opts;
  }, [opts, q]);
  const selected = opts.find(o => o.value === value);
  const showCustom = allowCustom && q.trim() && !opts.some(o => String(o.value).toLowerCase() === q.trim().toLowerCase());

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) { setOpen(false); setQ(''); } };
    // Escape must close ONLY the dropdown, not the parent Radix Sheet/Dialog.
    // Radix's DismissableLayer listens for Escape on the document in the CAPTURE
    // phase, which runs before React's synthetic onKeyDown — so we intercept on
    // window (outermost) capture and stop it from reaching Radix.
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.stopImmediatePropagation(); e.preventDefault(); setOpen(false); setQ(''); }
    };
    document.addEventListener('mousedown', h);
    window.addEventListener('keydown', onKey, true);
    const t = setTimeout(() => inputRef.current?.focus(), 10);
    return () => { document.removeEventListener('mousedown', h); window.removeEventListener('keydown', onKey, true); clearTimeout(t); };
  }, [open]);

  const pick = (v) => { onChange?.(v); setOpen(false); setQ(''); };

  return (
    <div ref={wrapRef} className={cn('relative', className)}>
      <button
        type="button" disabled={disabled} data-testid={testId}
        onClick={() => setOpen(o => !o)}
        className={cn('flex h-8 w-full items-center justify-between gap-1 rounded-md border border-input bg-background px-2 text-xs disabled:opacity-50', triggerClassName)}
      >
        <span className={cn('truncate', !selected && !(value && allowCustom) && 'text-muted-foreground')}>
          {selected ? selected.label : (value && allowCustom ? value : placeholder)}
        </span>
        <ChevronsUpDown className="h-3 w-3 shrink-0 opacity-50" />
      </button>
      {open && (
        <div className={cn('absolute z-[60] mt-1 w-max min-w-[260px] max-w-[460px] rounded-md border bg-popover shadow-md', align === 'right' ? 'right-0' : 'left-0')}>
          <div className="flex items-center border-b px-2">
            <Search className="h-3 w-3 shrink-0 opacity-50" />
            <input
              ref={inputRef} value={q} onChange={e => setQ(e.target.value)}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); if (filtered[0]) pick(filtered[0].value); else if (showCustom) pick(q.trim()); }
                if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); setQ(''); }
              }}
              placeholder={searchPlaceholder}
              className="h-7 w-full bg-transparent px-2 text-xs outline-none"
              data-testid={testId ? `${testId}-search` : undefined}
            />
          </div>
          <div className="max-h-52 overflow-y-auto py-1">
            {filtered.length === 0 && !showCustom && <div className="px-2 py-1.5 text-xs italic text-muted-foreground">No matches</div>}
            {filtered.map(o => (
              <button
                type="button" key={o.value} onClick={() => pick(o.value)}
                title={typeof o.label === 'string' ? o.label : undefined}
                className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-xs hover:bg-accent"
                data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
              >
                <Check className={cn('h-3 w-3 shrink-0', o.value === value ? 'opacity-100' : 'opacity-0')} />
                <span className="truncate">{o.label}</span>
                {o.sub && <span className="ml-auto text-[10px] text-muted-foreground font-mono truncate">{o.sub}</span>}
              </button>
            ))}
            {showCustom && (
              <button type="button" onClick={() => pick(q.trim())} className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-xs text-primary hover:bg-accent">
                <Plus className="h-3 w-3" /> Add “{q.trim()}”
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// Multi-value token input with a searchable dropdown of known options; also
// accepts free-form entries (Enter / comma). value is a string[]; onChange gets
// the new array. Inline dropdown (safe inside Sheet/Dialog).
export function MultiComboInput({
  value = [], onChange, options, placeholder = 'Add…', allowCustom = true, testId,
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const wrapRef = useRef(null);
  const inputRef = useRef(null);
  const opts = useMemo(() => normalize(options).slice().sort(byLabel), [options]);
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = s ? opts.filter(o => String(o.label).toLowerCase().includes(s) || String(o.value).toLowerCase().includes(s)) : opts;
    return base.filter(o => !value.includes(o.value));
  }, [opts, q, value]);
  const showCustom = allowCustom && q.trim() && !value.includes(q.trim()) && !opts.some(o => String(o.value).toLowerCase() === q.trim().toLowerCase());
  const labelOf = (v) => (opts.find(o => o.value === v)?.label) || v;

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    const onKey = (e) => {
      if (e.key === 'Escape') { e.stopPropagation(); e.stopImmediatePropagation(); e.preventDefault(); setOpen(false); }
    };
    document.addEventListener('mousedown', h);
    window.addEventListener('keydown', onKey, true);
    return () => { document.removeEventListener('mousedown', h); window.removeEventListener('keydown', onKey, true); };
  }, [open]);

  const add = (v) => { v = String(v).trim(); if (!v) return; if (!value.includes(v)) onChange?.([...value, v]); setQ(''); setOpen(false); };
  const remove = (v) => onChange?.(value.filter(x => x !== v));

  return (
    <div ref={wrapRef} className="relative">
      <div
        className="flex min-h-7 flex-wrap items-center gap-1 rounded-lg border border-border/30 bg-muted/30 p-1"
        onClick={() => { setOpen(true); inputRef.current?.focus(); }}
        data-testid={testId}
      >
        {value.map(v => (
          <span key={v} className="flex items-center gap-1 rounded bg-primary/10 px-1.5 py-0.5 text-[10px] text-primary" data-testid={testId ? `${testId}-token-${v}` : undefined}>
            {labelOf(v)}
            <button type="button" onClick={(e) => { e.stopPropagation(); remove(v); }} className="hover:text-destructive"><X className="h-2.5 w-2.5" /></button>
          </span>
        ))}
        <input
          ref={inputRef} value={q} onChange={e => setQ(e.target.value)} onFocus={() => setOpen(true)}
          onKeyDown={e => {
            if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); if (filtered[0]) add(filtered[0].value); else if (q.trim()) add(q.trim()); }
            if (e.key === 'Escape') { e.preventDefault(); e.stopPropagation(); setOpen(false); }
            if (e.key === 'Backspace' && !q && value.length) remove(value[value.length - 1]);
          }}
          placeholder={value.length ? '' : placeholder}
          className="min-w-[80px] flex-1 bg-transparent px-1 text-xs outline-none"
          data-testid={testId ? `${testId}-input` : undefined}
        />
      </div>
      {open && (filtered.length > 0 || showCustom) && (
        <div className="absolute left-0 z-[60] mt-1 max-h-52 w-full overflow-y-auto rounded-md border bg-popover py-1 shadow-md">
          {filtered.map(o => (
            <button
              type="button" key={o.value} onClick={() => add(o.value)}
              className="flex w-full items-center px-2 py-1.5 text-left text-xs hover:bg-accent"
              data-testid={testId ? `${testId}-opt-${o.value}` : undefined}
            >
              <span className="truncate">{o.label}</span>
              {o.sub && <span className="ml-auto text-[10px] text-muted-foreground truncate">{o.sub}</span>}
            </button>
          ))}
          {showCustom && (
            <button type="button" onClick={() => add(q.trim())} className="flex w-full items-center gap-1.5 px-2 py-1.5 text-left text-xs text-primary hover:bg-accent">
              <Plus className="h-3 w-3" /> Add “{q.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}
